import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDown, ChevronLeft, ChevronRight, X, Trash2,
  Upload, Camera, AlertCircle, Save, Check,
  BookCopy, Package, RotateCcw,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import {
  upsertBook,
  upsertBookMedia,
  deleteBookPhoto,
  listenToBookData,
  BookPage,
  MAX_PAGE_CHARS,
  MAX_PAGES,
} from "../../redux/bookSlice";
import { listenToUserCommercePartners } from "../../redux/commercePartnersSlice";

/* ═══════════════════════════════════════════════════════════════
   BOOKS CATEGORIES ONLY
═══════════════════════════════════════════════════════════════ */
import {
  idBooksCategories,
  idBooksSubcategories,
  idBooksProducts,
} from "../../CATEGORIES/BooksCategories";

/* ═══════════════════════════════════════════════════════════════
   DESIGN TOKENS — matches AddProducts.tsx
═══════════════════════════════════════════════════════════════ */
const T = {
  bg:           "#FFFFFF",
  bgSoft:       "#FCEFD4",
  bgMuted:      "#F3E6CE",
  pageBg:       "#FBF6EE",
  text:         "#1B0E06",
  textSoft:     "#1B0E06",
  muted:        "#7A6653",
  mutedDim:     "#A6947F",
  gold:         "#F2A93E",
  goldLight:    "#F7C874",
  red:          "#C8202E",
  orange:       "#D4900F",
  dark:         "#1B0E06",
  border:       "rgba(242,169,62,0.22)",
  borderOrange: "rgba(200,32,46,0.35)",
  white:        "#FFFFFF",
  green:        "#1E8E5A",
  purple:       "#7A56C9",
};
const GRAD      = "linear-gradient(90deg,#1B0E06 0%,#F2A93E 35%,#C8202E 65%,#9E1521 100%)";
const GRAD_SAVE = "linear-gradient(90deg,#F2A93E 0%,#C8202E 50%,#9E1521 100%)";
const GRAD_SIZE = "300% 100%";

const MAX_IMAGES = 10;

/* ═══════════════════════════════════════════════════════════════
   GLOBAL CSS — trimmed to what AddBook actually uses
═══════════════════════════════════════════════════════════════ */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700;800;900&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&family=Cormorant+Garamond:ital,wght@0,400;0,600;1,400&family=Source+Serif+4:opsz,wght@8..60,400;8..60,600&display=swap');
  :root{
    --pf-cover: #172433;
    --pf-cover-deep: #0d151f;
    --pf-gold: #b8935a;
    --pf-gold-bright: #d4b483;
    --pf-page: #f6f0e2;
    --pf-page-shadow: #e2d8c2;
    --pf-ink: #2b2620;
    --pf-ink-soft: #7a7161;
  }
  .pf-stage{
    display:flex; flex-direction:column; align-items:center; gap:16px;
    padding: 30px 14px 26px;
    border-radius: 18px;
    background: radial-gradient(ellipse at 50% 20%, #232830 0%, #0b0d10 78%);
    perspective: 2000px;
  }
  .pf-book{
    position:relative; width: 100%; max-width: 560px;
    aspect-ratio: 4 / 2.85;
    transform-style: preserve-3d;
  }
  .pf-book::after{
    content:""; position:absolute; left:8%; right:8%; bottom:-22px; height:22px;
    background: radial-gradient(ellipse at center, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0) 72%);
    filter: blur(2px);
  }
  .pf-spine{
    position:absolute; left:50%; top:0; bottom:0; width:28px; margin-left:-14px;
    background: linear-gradient(to right, rgba(0,0,0,0.16), rgba(0,0,0,0.32) 50%, rgba(0,0,0,0.16));
    z-index: 6; pointer-events:none;
  }
  .pf-half{ position:absolute; top:0; bottom:0; width:50%; background: var(--pf-page); overflow:hidden; }
  .pf-half.left{ left:0; border-radius: 6px 0 0 6px; }
  .pf-half.right{ right:0; border-radius: 0 6px 6px 0; }
  .pf-cover-face{
    position:absolute; inset:0;
    background: linear-gradient(155deg, var(--pf-cover) 0%, var(--pf-cover-deep) 100%);
    color: var(--pf-gold-bright);
    display:flex; flex-direction:column; align-items:center; justify-content:center; text-align:center;
    border: 1px solid rgba(184,147,90,0.4);
    padding: 22px;
  }
  .pf-cover-emblem{
    width:46px; height:46px; border:1.5px solid var(--pf-gold);
    border-radius:50%; display:flex; align-items:center; justify-content:center;
    margin-bottom:14px; font-family:'Cormorant Garamond', serif; font-size:20px; font-style:italic;
  }
  .pf-cover-face h1{
    font-family:'Cormorant Garamond', serif; font-weight:600; font-size:20px;
    letter-spacing:0.4px; margin:0 0 6px; line-height:1.25;
  }
  .pf-cover-face p{ font-size:10.5px; letter-spacing:2.4px; text-transform:uppercase; color:var(--pf-gold); margin:0; }
  .pf-cover-meta{
    margin-top:16px; display:flex; flex-direction:column; gap:3px; font-family:'Inter',sans-serif;
  }
  .pf-cover-meta span{ font-size:10px; color: rgba(212,180,131,0.75); letter-spacing:0.5px; }
  .pf-leaf{
    position:absolute; top:0; left:50%; width:50%; height:100%;
    transform-origin: left center; transform-style: preserve-3d;
    transition: transform 0.75s cubic-bezier(.4,.1,.2,1);
  }
  .pf-leaf.flipped{ transform: rotateY(-180deg); }
  .pf-leaf-face{
    position:absolute; inset:0; backface-visibility:hidden;
    background: var(--pf-page); box-shadow: 0 0 0 1px rgba(0,0,0,0.04);
    padding: 22px 22px 16px; display:flex; flex-direction:column;
    font-family:'Source Serif 4', serif; color: var(--pf-ink);
  }
  .pf-leaf-face.back{ transform: rotateY(180deg); background: var(--pf-page-shadow); }
  .pf-leaf-shade{
    position:absolute; inset:0; pointer-events:none;
    background: linear-gradient(to right, rgba(0,0,0,0.18), rgba(0,0,0,0) 40%);
    opacity:0; transition: opacity 0.75s ease;
  }
  .pf-leaf.flipped .pf-leaf-shade{ opacity:1; }
  .pf-page-textarea{
    flex:1; width:100%; border:none; outline:none; resize:none; background:transparent;
    font-family:'Source Serif 4', serif; font-size:13.5px; line-height:1.75; color: var(--pf-ink);
  }
  .pf-page-textarea::placeholder{ color: rgba(43,38,32,0.35); font-style:italic; }
  .pf-folio{
    margin-top:8px; padding-top:10px; border-top: 1px solid rgba(43,38,32,0.12);
    font-family:'Inter',sans-serif; font-size:10px; color: var(--pf-ink-soft); letter-spacing:1px;
    display:flex; justify-content:space-between; align-items:center;
  }
  .pf-back-label{
    display:flex; align-items:center; justify-content:center; height:100%;
    font-family:'Cormorant Garamond', serif; font-style:italic; font-size:15px;
    color: rgba(43,38,32,0.32); letter-spacing: 1px;
  }
  .pf-controls{
    display:flex; align-items:center; gap:14px;
    font-family:'Inter',sans-serif; color:#8a8f98; font-size:11px; letter-spacing:0.4px;
  }
  .pf-ribbon-track{ width:180px; height:3px; background: rgba(255,255,255,0.12); border-radius:2px; position:relative; }
  .pf-ribbon-fill{ position:absolute; left:0; top:0; height:100%; background: var(--pf-gold); border-radius:2px; transition: width 0.4s ease; }
  .pf-nav-btn{
    background:none; border:1px solid rgba(255,255,255,0.18); color:#c9cdd3;
    width:28px; height:28px; border-radius:50%; cursor:pointer;
    font-family:'Cormorant Garamond',serif; font-size:15px;
    display:flex; align-items:center; justify-content:center;
    transition: border-color 0.2s ease, color 0.2s ease;
  }
  .pf-nav-btn:hover:not(:disabled){ border-color: var(--pf-gold); color: var(--pf-gold-bright); }
  .pf-nav-btn:disabled{ opacity:0.3; cursor:default; }
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
  ::-webkit-scrollbar{width:4px;height:4px;}
  ::-webkit-scrollbar-track{background:transparent;}
  ::-webkit-scrollbar-thumb{background:rgba(242,169,62,0.25);border-radius:99px;}
  input,button,select,textarea{font-family:'DM Sans',sans-serif;}
  @keyframes arSpin{0%{background-position:0% 50%;}50%{background-position:100% 50%;}100%{background-position:0% 50%;}}
  @keyframes fadeUp{from{opacity:0;transform:translateY(12px);}to{opacity:1;transform:translateY(0);}}
  @keyframes shimmer{0%{background-position:-200% center;}100%{background-position:200% center;}}
  @keyframes pulse{0%,100%{opacity:1;}50%{opacity:.5;}}
  @keyframes stepIn{from{opacity:0;transform:translateY(6px);}to{opacity:1;transform:translateY(0);}}
  input::placeholder,textarea::placeholder{color:#A6947F;}
  .ap-input:focus{outline:none;border-color:rgba(200,32,46,.55)!important;box-shadow:0 0 0 3px rgba(200,32,46,.1)!important;}
  textarea.ap-input:focus{outline:none;border-color:rgba(200,32,46,.55)!important;box-shadow:0 0 0 3px rgba(200,32,46,.1)!important;}

  .ap-form-card{
    background:#fff;
    border:1.5px solid rgba(242,169,62,0.22);
    border-radius:20px;
    padding:26px 24px;
    animation:fadeUp .32s ease both;
    box-shadow:0 2px 20px rgba(242,169,62,0.07);
    transition:border-color .2s,box-shadow .2s;
  }
  .ap-form-card:hover{border-color:rgba(200,32,46,.16);box-shadow:0 4px 28px rgba(242,169,62,0.11);}
  .ap-form-card::before{
    content:'';display:block;height:3px;
    background:linear-gradient(90deg,#1B0E06 0%,#F2A93E 35%,#C8202E 65%,#9E1521 100%);
    background-size:300% 100%;animation:arSpin 6s linear infinite;
    border-radius:20px 20px 0 0;margin:-26px -24px 22px;
  }
  .ap-step{ animation:stepIn .28s ease both; }
  .ap-step + .ap-step{ margin-top:26px; padding-top:26px; border-top:1px solid rgba(242,169,62,0.18); }
  .ap-step-head{ display:flex; align-items:center; gap:10px; margin-bottom:14px; }
  .ap-step-num{
    width:24px;height:24px;border-radius:8px;flex-shrink:0;
    display:flex;align-items:center;justify-content:center;
    background:linear-gradient(135deg,#F2A93E,#C8202E);
    color:#fff;font-size:11px;font-weight:900;font-family:'Sora',sans-serif;
  }
  .sec-label{font-size:11px;font-weight:800;letter-spacing:.18em;background:linear-gradient(90deg,#1B0E06 0%,#F2A93E 40%,#C8202E 70%,#1B0E06 100%);background-size:200% auto;-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;animation:shimmer 5s linear infinite;}
  .media-thumb{width:82px;height:100px;position:relative;overflow:hidden;border-radius:12px;border:1.5px solid rgba(242,169,62,0.22);transition:transform .2s,box-shadow .2s;flex-shrink:0;}
  .media-thumb:hover{transform:translateY(-2px);box-shadow:0 6px 18px rgba(200,32,46,.18);}
  .add-media{width:82px;height:100px;border:1.5px dashed rgba(200,32,46,.4);border-radius:12px;background:rgba(200,32,46,.04);display:flex;flex-direction:column;align-items:center;justify-content:center;cursor:pointer;gap:5px;transition:border-color .2s,background .2s;flex-shrink:0;}
  .add-media:hover{border-color:rgba(200,32,46,.7);background:rgba(200,32,46,.09);}
  .vis-row{display:flex;align-items:center;justify-content:space-between;padding:14px 0;border-bottom:1px solid rgba(242,169,62,0.15);}
  .vis-row:last-child{border-bottom:none;padding-bottom:0;}
  .save-btn{display:flex;align-items:center;justify-content:center;gap:9px;border:none;border-radius:14px;padding:16px 0;width:100%;font-family:'Sora',sans-serif;font-weight:800;font-size:15px;cursor:pointer;transition:all .2s;letter-spacing:.03em;}
  .save-btn.active{background:linear-gradient(90deg,#F2A93E 0%,#C8202E 50%,#9E1521 100%);color:#fff;box-shadow:0 6px 24px rgba(200,32,46,.38);}
  .save-btn.inactive{background:#F3E6CE;color:#A6947F;cursor:not-allowed;}
  .save-btn.active:hover{transform:translateY(-1px);box-shadow:0 8px 28px rgba(200,32,46,.45);}
  .float-backdrop{position:fixed;inset:0;background:rgba(27,14,6,.18);z-index:9999;display:flex;align-items:center;justify-content:center;}
  .float-panel{background:#fff;border-radius:20px;border:1.5px solid rgba(200,32,46,.28);box-shadow:0 24px 80px rgba(27,14,6,.22), 0 4px 24px rgba(200,32,46,.15);overflow:hidden;display:flex;flex-direction:column;min-width:320px;max-width:440px;width:100%;max-height:72vh;z-index:10000;}
  .sheet-backdrop{position:fixed;inset:0;background:rgba(27,14,6,.38);z-index:9999;display:flex;align-items:flex-end;}
  .bottom-sheet{background:#fff;width:100%;max-height:70vh;border-top-left-radius:24px;border-top-right-radius:24px;border-top:1.5px solid rgba(200,32,46,.25);overflow:hidden;display:flex;flex-direction:column;}
  .sheet-list{overflow-y:auto;flex:1;}
  .ap-select{width:100%;border:1.5px solid rgba(242,169,62,0.22);border-radius:10px;background:#FCEFD4;color:#1B0E06;padding:11px 38px 11px 14px;font-size:13px;outline:none;appearance:none;cursor:pointer;transition:border-color .15s;font-family:'DM Sans',sans-serif;}
  .ap-select:focus{border-color:rgba(200,32,46,.55);box-shadow:0 0 0 3px rgba(200,32,46,.1);}
  .ap-select:disabled{opacity:.5;cursor:not-allowed;}
  .format-card{border:1.5px solid rgba(242,169,62,0.22);border-radius:16px;padding:18px;cursor:pointer;transition:all .18s;display:flex;flex-direction:column;gap:8px;flex:1;background:#FCEFD4;}
  .format-card:hover{border-color:rgba(200,32,46,.35);transform:translateY(-2px);}
  .format-card.sel{border-color:transparent;background:linear-gradient(180deg,rgba(200,32,46,.06),rgba(242,169,62,.06));box-shadow:0 0 0 2px #C8202E inset,0 8px 24px rgba(200,32,46,.14);}
  .page-shell{border:1.5px solid rgba(242,169,62,0.28);border-radius:18px;background:linear-gradient(180deg,#fff,#FEFBF4);box-shadow:0 10px 30px rgba(27,14,6,.06);overflow:hidden;}
  .page-nav-btn{display:flex;align-items:center;justify-content:center;gap:6px;padding:9px 16px;border-radius:10px;border:1.5px solid rgba(242,169,62,0.28);background:#fff;color:#1B0E06;font-weight:700;font-size:12.5px;cursor:pointer;transition:all .15s;font-family:'Sora',sans-serif;}
  .page-nav-btn:hover:not(:disabled){border-color:rgba(200,32,46,.45);background:rgba(200,32,46,.04);}
  .page-nav-btn:disabled{opacity:.35;cursor:not-allowed;}
  .page-nav-btn.primary{background:linear-gradient(90deg,#F2A93E 0%,#C8202E 50%,#9E1521 100%);color:#fff;border-color:transparent;box-shadow:0 4px 14px rgba(200,32,46,.28);}
  .page-nav-btn.primary:disabled{background:#F3E6CE;color:#A6947F;box-shadow:none;}
  .page-dot{width:6px;height:6px;border-radius:50%;background:rgba(242,169,62,0.35);flex-shrink:0;transition:all .15s;}
  .page-dot.active{background:#C8202E;width:16px;border-radius:4px;}
`;

/* ═══════════════════════════════════════════════════════════════
   TYPES
═══════════════════════════════════════════════════════════════ */
interface FormState {
  listingId?:    string;
  categoryId:    number | null;
  subcategoryId: number | null;
  productId:     number | null;

  title:       string;
  author:      string;
  description: string;

  isVisualBook: boolean | null;

  // physical
  price:     number;
  stock:     number;
  available: boolean;
  images:    File[];
  photos:    string[];
  dimensions: { unit: "cm" | "inch"; weightUnit: "g" | "ml"; height?: number; width?: number; length?: number; netWeight?: number };

  // visual
  pages: BookPage[];
  activePage: number;

  isPublished: boolean;
}

const uid = () => Math.random().toString(36).slice(2, 10);

const blank = (): FormState => ({
  categoryId: null, subcategoryId: null, productId: null,
  title: "", author: "", description: "",
  isVisualBook: null,
  price: 0, stock: 0, available: true, images: [], photos: [],
  dimensions: { unit: "cm", weightUnit: "g" },
  pages: [{ id: uid(), text: "" }],
  activePage: 0,
  isPublished: false,
});

/* ═══════════════════════════════════════════════════════════════
   UI PRIMITIVES (self-contained, mirrors AddProducts.tsx)
═══════════════════════════════════════════════════════════════ */
const Toggle: React.FC<{ on: boolean; onChange: (v: boolean) => void }> = ({ on, onChange }) => (
  <button
    onClick={() => onChange(!on)}
    style={{
      width: 48, height: 27, borderRadius: 14, border: "none", cursor: "pointer", position: "relative", flexShrink: 0,
      background: on ? GRAD_SAVE : "#A6947F", backgroundSize: on ? GRAD_SIZE : undefined,
      animation: on ? "arSpin 4s ease infinite" : "none", transition: "background .22s",
    }}
  >
    <div style={{ position: "absolute", top: 3.5, left: on ? 25 : 4, width: 20, height: 20, borderRadius: "50%", background: "#fff", transition: "left .22s", boxShadow: "0 1px 6px rgba(0,0,0,.25)" }} />
  </button>
);

const StepHead: React.FC<{ n: number; t: string; sub?: string }> = ({ n, t, sub }) => (
  <div className="ap-step-head">
    <div className="ap-step-num">{n}</div>
    <div>
      <span className="sec-label">{t.toUpperCase()}</span>
      {sub && <p style={{ fontSize: 11, color: T.muted, marginTop: 2, fontFamily: "'DM Sans',sans-serif" }}>{sub}</p>}
    </div>
  </div>
);

const SLabel: React.FC<{ t: string; sub?: string }> = ({ t, sub }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: sub ? 4 : 0 }}>
      <div style={{ width: 3, height: 16, borderRadius: 2, background: GRAD_SAVE, flexShrink: 0 }} />
      <span className="sec-label">{t.toUpperCase()}</span>
    </div>
    {sub && <p style={{ fontSize: 11, color: T.muted, marginTop: 3, marginLeft: 11, fontFamily: "'DM Sans',sans-serif" }}>{sub}</p>}
  </div>
);

const FL: React.FC<{ l: string; req?: boolean }> = ({ l, req }) => (
  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.15em", color: T.muted, marginBottom: 6, fontFamily: "'Sora',sans-serif" }}>
    {l.toUpperCase()}{req && <span style={{ color: T.orange, marginLeft: 3 }}>*</span>}
  </div>
);

const NIn: React.FC<{
  value: string | number; onChange: (v: string) => void;
  placeholder?: string; type?: string; rows?: number;
  prefix?: string; disabled?: boolean; suffix?: string;
}> = ({ value, onChange, placeholder, type = "text", rows, prefix, disabled, suffix }) => (
  <div style={{ display: "flex", border: `1.5px solid ${T.border}`, borderRadius: 10, background: disabled ? T.bgSoft : T.white, overflow: "hidden", opacity: disabled ? 0.55 : 1, transition: "border-color .15s" }}>
    {prefix && <div style={{ padding: "0 13px", display: "flex", alignItems: "center", background: T.bgSoft, borderRight: `1px solid ${T.border}`, fontSize: 13, color: T.gold, fontWeight: 700, flexShrink: 0 }}>{prefix}</div>}
    {rows
      ? <textarea className="ap-input" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={rows} disabled={disabled}
          style={{ flex: 1, border: "none", outline: "none", padding: "11px 14px", fontSize: 13, color: T.text, background: "transparent", resize: "vertical", lineHeight: 1.65, fontFamily: "'DM Sans',sans-serif" }} />
      : <input className="ap-input" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} type={type} disabled={disabled}
          style={{ flex: 1, border: "none", outline: "none", padding: "11px 14px", fontSize: 13, color: T.text, background: "transparent", fontFamily: "'DM Sans',sans-serif" }} />
    }
    {suffix && <div style={{ padding: "0 13px", display: "flex", alignItems: "center", fontSize: 12, color: T.muted, flexShrink: 0 }}>{suffix}</div>}
  </div>
);

function renderIconValue(icon: any, size = 18, color?: string): React.ReactNode {
  if (!icon) return null;
  if (React.isValidElement(icon)) return icon;
  if (typeof icon === "function") { const Icon = icon; return <Icon size={size} color={color} />; }
  return null;
}
const OptionGlyph: React.FC<{ icon?: any; image?: any; color?: string; size?: number }> = ({ icon, image, color, size = 20 }) => {
  const [imgError, setImgError] = useState(false);
  if (image && !imgError) return <img src={image} alt="" onError={() => setImgError(true)} style={{ width: size, height: size, borderRadius: 5, objectFit: "contain", flexShrink: 0 }} />;
  const rendered = renderIconValue(icon, size, color);
  if (rendered) return <span style={{ display: "flex", alignItems: "center", opacity: 0.75, flexShrink: 0 }}>{rendered}</span>;
  return null;
};

const NSel: React.FC<{
  value: number | string | null; onChange: (v: number) => void;
  options: { id: number; name: string; icon?: any; image?: any; color?: string }[];
  placeholder?: string; disabled?: boolean;
}> = ({ value, onChange, options, placeholder = "Select…", disabled }) => {
  const [open, setOpen] = useState(false);
  const [isMob, setIsMob] = useState(window.innerWidth < 900);
  const selected = options.find(o => o.id === value);
  useEffect(() => { const fn = () => setIsMob(window.innerWidth < 900); window.addEventListener("resize", fn); return () => window.removeEventListener("resize", fn); }, []);
  useEffect(() => { if (!open) return; const fn = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); }; window.addEventListener("keydown", fn); return () => window.removeEventListener("keydown", fn); }, [open]);

  const listItems = options.map(opt => (
    <button key={opt.id} onClick={() => { onChange(opt.id); setOpen(false); }}
      style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: isMob ? "14px 20px" : "10px 14px", border: "none", borderBottom: `1px solid ${T.border}`, background: value === opt.id ? "rgba(200,32,46,.06)" : "transparent", cursor: "pointer", fontFamily: "'DM Sans',sans-serif", borderRadius: isMob ? 0 : 8 }}>
      <span style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14, color: value === opt.id ? T.orange : T.text, fontWeight: value === opt.id ? 700 : 400 }}>
        <OptionGlyph icon={opt.icon} image={opt.image} color={opt.color} />
        {opt.name}
      </span>
      {value === opt.id && <Check size={16} color={T.orange} />}
    </button>
  ));
  const desktopModal = (
    <motion.div className="float-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setOpen(false)}>
      <motion.div className="float-panel" initial={{ scale: 0.96, opacity: 0, y: -10 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.96, opacity: 0, y: -10 }} transition={{ type: "spring", damping: 26, stiffness: 340 }} onClick={e => e.stopPropagation()}>
        <div style={{ height: 3, background: GRAD, backgroundSize: GRAD_SIZE, animation: "arSpin 5s linear infinite", flexShrink: 0 }} />
        <div style={{ padding: "14px 18px 10px", flexShrink: 0, borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <p style={{ fontSize: 14, fontWeight: 800, color: T.text, fontFamily: "'Sora',sans-serif" }}>{placeholder}</p>
          <button onClick={() => setOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", color: T.muted, display: "flex", padding: 4 }}><X size={16} /></button>
        </div>
        <div className="sheet-list" style={{ padding: "8px 10px" }}>{listItems}</div>
      </motion.div>
    </motion.div>
  );
  const mobileSheet = (
    <motion.div className="sheet-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setOpen(false)}>
      <motion.div className="bottom-sheet" initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }} transition={{ type: "spring", damping: 28, stiffness: 300 }} onClick={e => e.stopPropagation()}>
        <div style={{ height: 3, background: GRAD, backgroundSize: GRAD_SIZE, animation: "arSpin 5s linear infinite", flexShrink: 0 }} />
        <div style={{ width: 36, height: 4, borderRadius: 2, background: T.mutedDim, margin: "12px auto 8px", flexShrink: 0 }} />
        <p style={{ fontSize: 13, fontWeight: 800, color: T.text, padding: "4px 20px 10px", fontFamily: "'Sora',sans-serif", flexShrink: 0 }}>{placeholder}</p>
        <div className="sheet-list">{listItems}</div>
      </motion.div>
    </motion.div>
  );
  return (
    <>
      <button
        onClick={() => !disabled && setOpen(true)}
        disabled={disabled}
        style={{
          width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
          border: `1.5px solid ${open ? T.borderOrange : T.border}`, borderRadius: 10,
          background: disabled ? T.bgSoft : T.white,
          padding: "11px 14px", fontSize: 13, color: selected ? T.text : T.mutedDim,
          cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.55 : 1,
          fontFamily: "'DM Sans',sans-serif", transition: "border-color .15s",
          boxShadow: open ? "0 0 0 3px rgba(200,32,46,.1)" : "none",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {selected && <OptionGlyph icon={selected.icon} image={selected.image} color={selected.color} size={18} />}
          {selected?.name ?? placeholder}
        </span>
        <ChevronDown size={14} color={T.muted} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .2s" }} />
      </button>
      {createPortal(<AnimatePresence>{open && (isMob ? mobileSheet : desktopModal)}</AnimatePresence>, document.body)}
    </>
  );
};

const Toast: React.FC<{ visible: boolean; msg: string; type: "ok" | "err" | "warn" }> = ({ visible, msg, type }) => (
  <AnimatePresence>
    {visible && (
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
        style={{
          position: "fixed", bottom: 32, left: "50%", transform: "translateX(-50%)",
          background: type === "ok" ? GRAD_SAVE : type === "err" ? "#C8202E" : T.gold,
          color: "#fff", fontSize: 13, fontWeight: 700, letterSpacing: "0.06em",
          padding: "13px 28px", borderRadius: 999, zIndex: 99999,
          boxShadow: "0 8px 32px rgba(27,14,6,.18)", whiteSpace: "nowrap",
          fontFamily: "'Sora',sans-serif", display: "flex", alignItems: "center", gap: 8,
        }}
      >
        {type === "ok" ? <Check size={14} /> : <AlertCircle size={14} />}
        {msg}
      </motion.div>
    )}
  </AnimatePresence>
);

/* ═══════════════════════════════════════════════════════════════
   PAGE WRITER — real two-page flip-book editor, styled after the
   page-flip demo: dark stage, cover face, leaves that rotateY as
   they turn. Leaf i is "flipped" (turned to the left) once the
   writer has moved past page i. The active (right-hand, unflipped)
   leaf is the only one that's actually editable.
═══════════════════════════════════════════════════════════════ */
const PageWriter: React.FC<{
  title: string;
  author: string;
  pages: BookPage[];
  activePage: number;
  onChangeText: (idx: number, text: string) => void;
  onNav: (idx: number) => void;
  onAddPage: () => void;
  onDeletePage: (idx: number) => void;
}> = ({ title, author, pages, activePage, onChangeText, onNav, onAddPage, onDeletePage }) => {
  const total   = pages.length;
  const current = pages[activePage];
  const isLast  = activePage === total - 1;
  const chars   = current?.text.length ?? 0;
  const canAddMore = total < MAX_PAGES;
  const [animating, setAnimating] = useState(false);

  const goTo = (idx: number) => {
    if (animating || idx < 0 || idx >= total || idx === activePage) return;
    setAnimating(true);
    onNav(idx);
    setTimeout(() => setAnimating(false), 760);
  };

  const handleNext = () => {
    if (animating) return;
    if (!isLast) { goTo(activePage + 1); return; }
    if (!current?.text.trim() || !canAddMore) return;
    setAnimating(true);
    onAddPage();
    setTimeout(() => setAnimating(false), 760);
  };
  const handlePrev = () => goTo(activePage - 1);

  useEffect(() => {
    const fn = (e: KeyboardEvent) => {
      if (document.activeElement?.tagName === "TEXTAREA") return;
      if (e.key === "ArrowRight") handleNext();
      if (e.key === "ArrowLeft") handlePrev();
    };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePage, total, animating]);

  return (
    <div>
      <div className="pf-stage">
        <div className="pf-book">
          {/* static left half — book info plate, always behind the leaves */}
          <div className="pf-half left">
            <div className="pf-cover-face">
              <div className="pf-cover-emblem">{(title || "?").trim().charAt(0).toUpperCase() || "?"}</div>
              <h1>{title || "Untitled Book"}</h1>
              <p>{author ? `by ${author}` : "Visual Book"}</p>
              <div className="pf-cover-meta">
                <span>PAGE {activePage + 1} OF {total}</span>
                <span>{chars}/{MAX_PAGE_CHARS} CHARACTERS</span>
              </div>
            </div>
          </div>
          {/* static right half — shows through once every leaf is flipped past it */}
          <div className="pf-half right">
            <div className="pf-cover-face" style={{ opacity: 0.001 }} />
          </div>

          <div className="pf-spine" />

          {pages.map((p, i) => {
            const isFlipped = i < activePage;
            const isActive  = i === activePage;
            return (
              <div
                key={p.id}
                className={`pf-leaf${isFlipped ? " flipped" : ""}`}
                style={{ zIndex: isFlipped ? i + 1 : total - i }}
              >
                <div className="pf-leaf-face front">
                  {isActive ? (
                    <>
                      <textarea
                        className="pf-page-textarea"
                        autoFocus={false}
                        value={p.text}
                        onChange={e => onChangeText(i, e.target.value.slice(0, MAX_PAGE_CHARS))}
                        placeholder={`Start writing page ${i + 1}…`}
                      />
                      <div className="pf-folio">
                        <span>{author || "—"}</span>
                        <span>{i + 1}</span>
                      </div>
                    </>
                  ) : (
                    <div className="pf-back-label">{i + 1}</div>
                  )}
                </div>
                <div className="pf-leaf-face back">
                  <div className="pf-back-label">{i + 1}</div>
                </div>
                <div className="pf-leaf-shade" />
              </div>
            );
          })}
        </div>

        <div className="pf-controls">
          <button className="pf-nav-btn" onClick={handlePrev} disabled={activePage === 0 || animating} aria-label="Previous page">‹</button>
          <div className="pf-ribbon-track"><div className="pf-ribbon-fill" style={{ width: `${(activePage / Math.max(total - 1, 1)) * 100}%` }} /></div>
          <button
            className="pf-nav-btn"
            onClick={handleNext}
            disabled={animating || (isLast && (!current?.text.trim() || !canAddMore))}
            aria-label="Next page"
          >›</button>
        </div>
      </div>

      {/* below-book controls */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
        <button
          onClick={() => goTo(0)}
          disabled={activePage === 0 || animating}
          style={{ display: "flex", alignItems: "center", gap: 5, background: "none", border: "none", cursor: activePage === 0 ? "default" : "pointer", color: activePage === 0 ? T.mutedDim : T.orange, fontSize: 11.5, fontWeight: 700, fontFamily: "'Sora',sans-serif", opacity: activePage === 0 ? 0.5 : 1 }}
        >
          <RotateCcw size={12} /> Page 1
        </button>
        <div style={{ display: "flex", gap: 10 }}>
          {total > 1 && (
            <button onClick={() => onDeletePage(activePage)} style={{ background: "none", border: "none", cursor: "pointer", color: T.red, fontSize: 11.5, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4, fontFamily: "'Sora',sans-serif", padding: 0 }}>
              <Trash2 size={12} /> Delete this page
            </button>
          )}
          <button className="page-nav-btn" onClick={handlePrev} disabled={activePage === 0 || animating}>
            <ChevronLeft size={14} /> Back
          </button>
          <button
            className="page-nav-btn primary"
            onClick={handleNext}
            disabled={animating || (isLast && (!current?.text.trim() || !canAddMore))}
            title={isLast && !canAddMore ? `Limit of ${MAX_PAGES} pages reached` : undefined}
          >
            {isLast ? "New Page" : "Next"} <ChevronRight size={14} />
          </button>
        </div>
      </div>
      {isLast && !canAddMore && (
        <p style={{ textAlign: "center", fontSize: 11, color: T.red, marginTop: 10, fontFamily: "'DM Sans',sans-serif" }}>
          You've reached the {MAX_PAGES}-page limit for this book.
        </p>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════ */
const AddBook: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { listingId: pid } = useParams<{ listingId?: string }>();
  const userId          = useSelector((s: RootState) => s.auth.user?.userId);
  const partners        = useSelector((s: RootState) => s.commercePartners.partners);
  const partnerLoading  = useSelector((s: RootState) => s.commercePartners.loading);
  const currentBook     = useSelector((s: RootState) => s.books.currentBook);

  const [form,   setForm]   = useState<FormState>(blank());
  const [saving, setSaving] = useState(false);
  const [dirty,  setDirty]  = useState(false);
  const [isMob,  setIsMob]  = useState(window.innerWidth < 900);
  const [snack,  setSnack]  = useState<{ open: boolean; msg: string; type: "ok" | "err" | "warn" }>({ open: false, msg: "", type: "ok" });

  const toast = (msg: string, type: "ok" | "err" | "warn" = "ok") => {
    setSnack({ open: true, msg, type });
    setTimeout(() => setSnack(s => ({ ...s, open: false })), 3000);
  };

  useEffect(() => {
    const fn = () => setIsMob(window.innerWidth < 900);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  useEffect(() => { if (userId) dispatch(listenToUserCommercePartners(userId) as any); }, [userId, dispatch]);
  useEffect(() => {
    if (!userId || partnerLoading) return;
    if (partners?.length === 0) navigate(`/CommerceOnboarding/${userId}`, { replace: true });
  }, [partners, partnerLoading, userId, navigate]);
  useEffect(() => { if (pid) dispatch(listenToBookData(pid) as any); }, [dispatch, pid]);

  useEffect(() => {
    if (!pid || !currentBook) return;
    setForm({
      listingId:     currentBook.listingId,
      categoryId:    currentBook.categoryId ?? null,
      subcategoryId: currentBook.subcategoryId ?? null,
      productId:     currentBook.productId ?? null,
      title:         currentBook.title ?? "",
      author:        currentBook.author ?? "",
      description:   currentBook.description ?? "",
      isVisualBook:  currentBook.isVisualBook ?? null,
      price:         currentBook.price ?? 0,
      stock:         currentBook.stock ?? 0,
      available:     currentBook.available ?? true,
      images: [], photos: currentBook.photos ?? [],
      dimensions: currentBook.dimensions ?? { unit: "cm", weightUnit: "g" },
      pages: currentBook.pages && currentBook.pages.length > 0 ? currentBook.pages : [{ id: uid(), text: "" }],
      activePage: 0,
      isPublished: currentBook.isPublished ?? false,
    });
    setDirty(false);
  }, [currentBook, pid]);

  const up = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm(p => ({ ...p, [key]: value }));
    setDirty(true);
  };

  /* ── category derivations (Books only) ── */
  const subs  = (idBooksSubcategories as any[]).filter(s => s.parentId === form.categoryId);
  const prods = (idBooksProducts as any[]).filter(p => p.parentId === form.subcategoryId);
  const isEdit = !!pid;

  /* ── page helpers ── */
  const setPageText = (idx: number, text: string) => {
    const next = [...form.pages];
    next[idx] = { ...next[idx], text };
    up("pages", next);
  };
  const navToPage = (idx: number) => {
    if (idx < 0 || idx >= form.pages.length) return;
    up("activePage", idx);
  };
  const addPage = () => {
    if (form.pages.length >= MAX_PAGES) { toast(`Limit of ${MAX_PAGES} pages reached`, "warn"); return; }
    const next = [...form.pages, { id: uid(), text: "" }];
    setForm(p => ({ ...p, pages: next, activePage: next.length - 1 }));
    setDirty(true);
  };
  const deletePage = (idx: number) => {
    if (form.pages.length <= 1) return;
    const next = form.pages.filter((_, i) => i !== idx);
    const newActive = Math.max(0, Math.min(form.activePage, next.length - 1));
    setForm(p => ({ ...p, pages: next, activePage: newActive }));
    setDirty(true);
  };

  const handleImages = (files: FileList | null) => {
    if (!files) return;
    const remaining = MAX_IMAGES - form.photos.length - form.images.length;
    if (remaining <= 0) { toast("Maximum 10 photos allowed", "warn"); return; }
    up("images", [...form.images, ...Array.from(files).slice(0, remaining)]);
  };
  const handleDelPhoto = (url: string) => {
    if (!form.listingId) return;
    dispatch(deleteBookPhoto({ listingId: form.listingId, photoUrl: url }));
    up("photos", form.photos.filter(p => p !== url));
  };

  const coverFileRef = useRef<HTMLInputElement>(null);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const currentCover = coverPreview ?? currentBook?.coverPhoto ?? null;

  const handleSave = async () => {
    if (!form.title.trim()) { toast("Title is required", "warn"); return; }
    if (form.isVisualBook === null) { toast("Choose Physical or Visual first", "warn"); return; }

    setSaving(true);
    try {
      const commonData: any = {
        title: form.title,
        author: form.author,
        description: form.description,
        categoryId: form.categoryId,
        subcategoryId: form.subcategoryId,
        productId: form.productId,
        isVisualBook: form.isVisualBook,
        isPublished: form.isPublished,
        isDraft: !form.isPublished,
      };

      if (form.isVisualBook) {
        commonData.pages = form.pages.filter(p => p.text.trim().length > 0).length > 0
          ? form.pages
          : form.pages; // keep at least the working page even if empty
      } else {
        commonData.price = form.price;
        commonData.stock = form.stock;
        commonData.available = form.available;
        commonData.dimensions = form.dimensions ?? {};
      }

      const saved = await dispatch(upsertBook({ listingId: form.listingId, data: commonData })).unwrap();

      if (!form.isVisualBook) {
        if (coverFile) {
          await dispatch(upsertBookMedia({ listingId: saved.listingId, files: [coverFile], type: "cover" })).unwrap();
          setCoverFile(null);
        }
        if (form.images.length > 0) {
          await dispatch(upsertBookMedia({ listingId: saved.listingId, files: form.images, type: "photo" })).unwrap();
        }
      }

      toast("Saved successfully ✓");
      setForm(p => ({ ...p, listingId: saved.listingId, images: [] }));
      setDirty(false);
      if (!pid) navigate(`/AddBook/${saved.listingId}`, { replace: true });
    } catch (err) {
      console.error(err);
      toast("Save failed", "err");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: T.pageBg, fontFamily: "'DM Sans',sans-serif", paddingBottom: 100 }}>
      <style>{CSS}</style>

      {/* ── STICKY HEADER ── */}
      <div style={{ background: T.bg, borderBottom: `1px solid ${T.border}`, position: "sticky", top: 0, zIndex: 100, boxShadow: "0 2px 16px rgba(242,169,62,0.08)" }}>
        <div style={{ height: 3, background: GRAD, backgroundSize: GRAD_SIZE, animation: "arSpin 5s linear infinite" }} />
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "13px 20px", maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button
              onClick={() => navigate(-1)}
              style={{ width: 36, height: 36, border: `1.5px solid ${T.border}`, borderRadius: 10, background: T.bgSoft, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: T.muted }}
            >
              <ChevronLeft size={20} />
            </button>
            <div>
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.22em", color: T.mutedDim, fontFamily: "'Sora',sans-serif" }}>
                {isEdit ? "EDITING BOOK" : "NEW BOOK"}
              </div>
              <h1 style={{ fontWeight: 900, fontSize: isMob ? 17 : 21, color: T.text, letterSpacing: -0.3, lineHeight: 1.15, fontFamily: "'Sora',sans-serif" }}>
                {isEdit ? (form.title || "Edit Book") : "Add Book"}
              </h1>
            </div>
          </div>
          {!isMob && (
            <button
              className={`save-btn${dirty ? " active" : " inactive"}`}
              onClick={dirty && !saving ? handleSave : undefined}
              style={{ width: "auto", padding: "12px 32px", borderRadius: 12, fontSize: 14 }}
              disabled={!dirty || saving}
            >
              {saving ? <><span style={{ animation: "pulse 1s infinite" }}>●</span> Saving…</> : <><Save size={15} /> Save Book</>}
            </button>
          )}
        </div>
      </div>

      {/* ── BODY ── */}
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: isMob ? "16px 14px" : "24px 28px" }}>
        <div className={isMob ? "" : "ap-desktop-grid"} style={!isMob ? { display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: 20, alignItems: "start" } : undefined}>

          <div>
            <div className="ap-form-card">

              {/* ── STEP 1 — CATEGORY ── */}
              <div className="ap-step">
                <StepHead n={1} t="Category" sub="Pick a books category" />
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <div>
                    <FL l="Category" req />
                    <NSel
                      value={form.categoryId}
                      onChange={v => { setForm(p => ({ ...p, categoryId: v, subcategoryId: null, productId: null })); setDirty(true); }}
                      options={(idBooksCategories as any[]).map(c => ({ id: c.id, name: c.name, icon: c.icon, image: c.image, color: c.color }))}
                      placeholder="Select category…"
                    />
                  </div>
                  {subs.length > 0 && (
                    <div>
                      <FL l="Subcategory" req />
                      <NSel
                        value={form.subcategoryId}
                        onChange={v => { setForm(p => ({ ...p, subcategoryId: v, productId: null })); setDirty(true); }}
                        options={subs.map(s => ({ id: s.id, name: s.name, icon: s.icon, image: s.image, color: s.color }))}
                        placeholder="Select subcategory…"
                        disabled={!form.categoryId}
                      />
                    </div>
                  )}
                  {prods.length > 0 && (
                    <div>
                      <FL l="Genre / Type" />
                      <NSel
                        value={form.productId}
                        onChange={v => up("productId", v)}
                        options={prods.map(p => ({ id: p.id, name: p.name, icon: p.icon, image: p.image, color: p.color }))}
                        placeholder="Select genre…"
                        disabled={!form.subcategoryId}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* ── STEP 2 — DETAILS ── */}
              <div className="ap-step">
                <StepHead n={2} t="Details" sub="Title, author and description" />
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  <div>
                    <FL l="Title" req />
                    <NIn value={form.title} onChange={v => up("title", v)} placeholder="Book title…" />
                  </div>
                  <div>
                    <FL l="Author" />
                    <NIn value={form.author} onChange={v => up("author", v)} placeholder="Author name…" />
                  </div>
                  <div>
                    <FL l="Description" />
                    <NIn value={form.description} onChange={v => up("description", v)} placeholder="Describe your book…" rows={4} />
                  </div>
                </div>
              </div>

              {/* ── STEP 3 — FORMAT ── */}
              <div className="ap-step">
                <StepHead n={3} t="Format" sub="Is this a physical copy or a book written directly here?" />
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                  <div
                    className={`format-card${form.isVisualBook === false ? " sel" : ""}`}
                    onClick={() => up("isVisualBook", false)}
                  >
                    <Package size={22} color={form.isVisualBook === false ? T.red : T.muted} />
                    <span style={{ fontSize: 14, fontWeight: 800, color: T.text, fontFamily: "'Sora',sans-serif" }}>Physical / PDF Copy</span>
                    <span style={{ fontSize: 11.5, color: T.muted, lineHeight: 1.5, fontFamily: "'DM Sans',sans-serif" }}>
                      Sold like a normal product — cover photo, price and stock.
                    </span>
                  </div>
                  <div
                    className={`format-card${form.isVisualBook === true ? " sel" : ""}`}
                    onClick={() => up("isVisualBook", true)}
                  >
                    <BookCopy size={22} color={form.isVisualBook === true ? T.red : T.muted} />
                    <span style={{ fontSize: 14, fontWeight: 800, color: T.text, fontFamily: "'Sora',sans-serif" }}>Visual Book</span>
                    <span style={{ fontSize: 11.5, color: T.muted, lineHeight: 1.5, fontFamily: "'DM Sans',sans-serif" }}>
                      Write the book page by page — readers flip through it here.
                    </span>
                  </div>
                </div>
              </div>

              {/* ── STEP 4a — PHYSICAL: pricing & stock ── */}
              {form.isVisualBook === false && (
                <>
                  <div className="ap-step">
                    <StepHead n={4} t="Pricing & Stock" />
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                      <div>
                        <FL l="Price" req />
                        <NIn value={form.price} onChange={v => up("price", +v)} type="number" prefix="€" placeholder="0.00" />
                      </div>
                      <div>
                        <FL l="Stock" />
                        <NIn value={form.stock} onChange={v => up("stock", +v)} type="number" placeholder="0" suffix="units" />
                      </div>
                    </div>
                  </div>

                  {/* ── STEP 5a — MEDIA ── */}
                  <div className="ap-step">
                    <StepHead n={5} t="Cover & Photos" sub={`${form.photos.length + form.images.length}/${MAX_IMAGES} extra photos`} />
                    <div style={{ marginBottom: 14 }}>
                      <FL l="Cover Photo" req />
                      <div
                        onClick={() => coverFileRef.current?.click()}
                        style={{
                          width: 110, height: 150, borderRadius: 12, background: T.bgMuted,
                          border: `1.5px dashed rgba(200,32,46,.4)`,
                          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                          cursor: "pointer", overflow: "hidden", gap: 5,
                        }}
                      >
                        {currentCover
                          ? <img src={currentCover} alt="cover" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          : <><Camera size={18} color={T.orange} /><span style={{ fontSize: 9, color: T.muted, letterSpacing: "0.1em", fontWeight: 700, fontFamily: "'Sora',sans-serif" }}>COVER</span></>
                        }
                        <input ref={coverFileRef} type="file" hidden accept="image/*" onChange={e => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          setCoverFile(f);
                          setCoverPreview(URL.createObjectURL(f));
                          setDirty(true);
                        }} />
                      </div>
                    </div>

                    <FL l="Extra Photos" />
                    <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 4 }}>
                      {form.photos.map(url => (
                        <div key={url} className="media-thumb">
                          <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          <button
                            onClick={() => handleDelPhoto(url)}
                            style={{ position: "absolute", top: 4, right: 4, width: 20, height: 20, borderRadius: "50%", background: "rgba(200,32,46,.85)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                          >
                            <Trash2 size={10} color="#fff" />
                          </button>
                        </div>
                      ))}
                      {form.images.map((f, i) => (
                        <div key={i} className="media-thumb">
                          <img src={URL.createObjectURL(f)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          <button
                            onClick={() => up("images", form.images.filter((_, j) => j !== i))}
                            style={{ position: "absolute", top: 4, right: 4, width: 20, height: 20, borderRadius: "50%", background: "rgba(200,32,46,.85)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                          >
                            <Trash2 size={10} color="#fff" />
                          </button>
                        </div>
                      ))}
                      {(form.photos.length + form.images.length) < MAX_IMAGES && (
                        <label className="add-media">
                          <Upload size={18} color={T.orange} />
                          <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", color: T.orange, fontFamily: "'Sora',sans-serif" }}>PHOTO</span>
                          <input type="file" hidden multiple accept="image/*" onChange={e => handleImages(e.target.files)} />
                        </label>
                      )}
                    </div>
                  </div>

                  {/* ── STEP 6a — DIMENSIONS ── */}
                  <div className="ap-step">
                    <StepHead n={6} t="Dimensions" sub="Helps calculate shipping" />
                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                        <div>
                          <FL l="Unit" />
                          <div style={{ position: "relative" }}>
                            <select className="ap-select" value={form.dimensions?.unit ?? "cm"} onChange={e => up("dimensions", { ...form.dimensions!, unit: e.target.value as "cm" | "inch" })}>
                              <option value="cm">cm</option>
                              <option value="inch">inch</option>
                            </select>
                            <ChevronDown size={13} color={T.muted} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} />
                          </div>
                        </div>
                        <div>
                          <FL l="Weight unit" />
                          <div style={{ position: "relative" }}>
                            <select className="ap-select" value={form.dimensions?.weightUnit ?? "g"} onChange={e => up("dimensions", { ...form.dimensions!, weightUnit: e.target.value as "g" | "ml" })}>
                              <option value="g">g</option>
                              <option value="ml">ml</option>
                            </select>
                            <ChevronDown size={13} color={T.muted} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} />
                          </div>
                        </div>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                        <div>
                          <FL l={`Height (${form.dimensions?.unit ?? "cm"})`} />
                          <NIn value={form.dimensions?.height ?? ""} onChange={v => up("dimensions", { ...form.dimensions!, height: +v })} type="number" placeholder="0" />
                        </div>
                        <div>
                          <FL l={`Width (${form.dimensions?.unit ?? "cm"})`} />
                          <NIn value={form.dimensions?.width ?? ""} onChange={v => up("dimensions", { ...form.dimensions!, width: +v })} type="number" placeholder="0" />
                        </div>
                        <div>
                          <FL l={`Length (${form.dimensions?.unit ?? "cm"})`} />
                          <NIn value={form.dimensions?.length ?? ""} onChange={v => up("dimensions", { ...form.dimensions!, length: +v })} type="number" placeholder="0" />
                        </div>
                        <div>
                          <FL l={`Net weight (${form.dimensions?.weightUnit ?? "g"})`} />
                          <NIn value={form.dimensions?.netWeight ?? ""} onChange={v => up("dimensions", { ...form.dimensions!, netWeight: +v })} type="number" placeholder="0" />
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* ── STEP 4b — VISUAL: write the book ── */}
              {form.isVisualBook === true && (
                <div className="ap-step">
                  <StepHead n={4} t="Write Your Book" sub={`Up to ${MAX_PAGE_CHARS} characters per page, ${MAX_PAGES} pages max`} />
                  <PageWriter
                    title={form.title}
                    author={form.author}
                    pages={form.pages}
                    activePage={form.activePage}
                    onChangeText={setPageText}
                    onNav={navToPage}
                    onAddPage={addPage}
                    onDeletePage={deletePage}
                  />
                </div>
              )}

            </div>
          </div>

          {/* ════════════════════════════════
              RIGHT COLUMN — Publish sidebar
          ════════════════════════════════ */}
          <div style={!isMob ? { position: "sticky", top: 80 } : undefined}>
            <div className="ap-form-card">
              <SLabel t="Publish" />
              <div style={{ marginBottom: 16 }}>
                {form.isVisualBook === false && (
                  <div className="vis-row">
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: T.text, fontFamily: "'DM Sans',sans-serif" }}>Available</div>
                      <p style={{ fontSize: 11, color: T.muted, marginTop: 2, fontFamily: "'DM Sans',sans-serif" }}>Buyers can view & purchase</p>
                    </div>
                    <Toggle on={form.available} onChange={v => up("available", v)} />
                  </div>
                )}
                <div className="vis-row">
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: T.text, fontFamily: "'DM Sans',sans-serif" }}>Published</div>
                    <p style={{ fontSize: 11, color: T.muted, marginTop: 2, fontFamily: "'DM Sans',sans-serif" }}>Visible in marketplace search</p>
                  </div>
                  <Toggle on={form.isPublished} onChange={v => up("isPublished", v)} />
                </div>
              </div>

              <button
                className={`save-btn${dirty ? " active" : " inactive"}`}
                onClick={dirty && !saving ? handleSave : undefined}
                disabled={!dirty || saving}
              >
                {saving
                  ? <><span style={{ animation: "pulse 1s infinite" }}>●</span> Saving…</>
                  : <><Save size={16} /> {isEdit ? "Update Book" : "Save Book"}</>
                }
              </button>
              {form.listingId && (
                <p style={{ textAlign: "center", fontSize: 11, color: T.muted, marginTop: 10, fontFamily: "'DM Sans',sans-serif" }}>
                  ID: <span style={{ fontFamily: "monospace", color: T.gold }}>{form.listingId}</span>
                </p>
              )}

              {isEdit && (
                <>
                  <div style={{ height: 1, background: T.border, margin: "20px 0 16px" }} />
                  <SLabel t="Summary" />
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {[
                      { label: "Title",  value: form.title || "—" },
                      { label: "Author", value: form.author || "—" },
                      { label: "Format", value: form.isVisualBook === null ? "—" : form.isVisualBook ? "Visual Book" : "Physical" },
                      form.isVisualBook
                        ? { label: "Pages", value: String(form.pages.length) }
                        : { label: "Price", value: form.price ? `€${form.price}` : "—" },
                      { label: "Status", value: form.isPublished ? "Published" : "Draft" },
                    ].map(row => (
                      <div key={row.label} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "5px 0", borderBottom: `1px solid ${T.border}` }}>
                        <span style={{ color: T.muted, fontFamily: "'DM Sans',sans-serif" }}>{row.label}</span>
                        <span style={{ color: T.text, fontWeight: 600, fontFamily: "'DM Sans',sans-serif" }}>{row.value}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

        </div>
      </div>

      {/* ── MOBILE STICKY SAVE ── */}
      {isMob && (
        <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, padding: "12px 16px 20px", background: T.bg, borderTop: `1px solid ${T.border}`, zIndex: 90, boxShadow: "0 -4px 20px rgba(242,169,62,0.1)" }}>
          <button
            className={`save-btn${dirty ? " active" : " inactive"}`}
            onClick={dirty && !saving ? handleSave : undefined}
            disabled={!dirty || saving}
          >
            {saving
              ? <><span style={{ animation: "pulse 1s infinite" }}>●</span> Saving…</>
              : <><Save size={16} /> {isEdit ? "Update Book" : "Save Book"}</>
            }
          </button>
        </div>
      )}

      <Toast visible={snack.open} msg={snack.msg} type={snack.type} />
    </div>
  );
};

export default AddBook;